import React, { useEffect, useMemo, useState } from "react";

const PLACEHOLDER = "Not available in extracted text";

function cleanValue(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function extractField(text, patterns) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (let i = 0; i < lines.length; i += 1) {
    for (const pattern of patterns) {
      const match = lines[i].match(pattern);
      if (match?.[1]) return cleanValue(match[1]);
      if (match && !match[1] && lines[i + 1]) return cleanValue(lines[i + 1]);
    }
  }
  return "";
}
function maskAadhaar(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function extractAddress(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const startIndex = lines.findIndex((line) =>
    /^(?:address|पता)\s*[:\-]?/i.test(line)
  );

  if (startIndex === -1) {
    // Also handle OCR that puts the address label mid-line.
    const match = String(text || "").match(
      /(?:address|पता)\s*[:\-]?\s*([\s\S]+)/i
    );
    if (!match) return "";

    return match[1]
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .join(", ")
      .trim();
  }

  const firstLine = lines[startIndex].replace(
    /^(?:address|पता)\s*[:\-]?\s*/i,
    ""
  );

  const addressLines = firstLine ? [firstLine] : [];

  for (let i = startIndex + 1; i < lines.length; i += 1) {
    const line = lines[i];

    if (
      /^(?:name|dob|date of birth|gender|aadhaar number|mobile|email)\s*[:\-]?/i.test(
        line
      )
    ) {
      break;
    }

    addressLines.push(line);

    // Include the PIN-code line, then stop.
    if (/\b\d{6}\b/.test(line)) break;
  }

  return addressLines.join(", ").trim();
}

function parseDetails(documents) {
  const allText = documents.map((doc) => doc.extracted_text || "").join("\n");
  const front = documents.find((doc) => /front/i.test(doc.document_type || "")) || documents[0] || {};
  const text = front.extracted_text || allText;
  const name = extractField(text, [
    /(?:name)\s*[:\-]?\s*(.+)$/i,
    /(?:नाम)\s*[:\-]?\s*(.+)$/i,
  ]);
  const dob = extractField(text, [
    /(?:date of birth|dob|birth)\s*[:\-]?\s*(.+)$/i,
    /(?:जन्म तिथि|जन्म)\s*[:\-]?\s*(.+)$/i,
  ]);
  const gender = extractField(text, [
    /\b(male|female|transgender)\b/i,
    /\b(पुरुष|महिला|अन्य)\b/i,
  ]);
  const aadhaar = (allText.match(/\b(?:\d{4}[ -]?){2}\d{4}\b/) || [])[0] || "";
  return {
    name: name || "",
    dob: dob || "",
    gender: gender || "",
    aadhaar: aadhaar ? maskAadhaar(aadhaar) : "",
    address: extractAddress(allText),
  };
}

function AadhaarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="aadhaar-icon-svg">
      <rect x="3" y="4.5" width="18" height="15" rx="2.1" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="8" cy="10" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 15c.6-2 4.4-2 5 0M13 9h5M13 12h5M13 15h3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function AadhaarDetailsPopup({ userId, userName, apiBaseUrl }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [identity, setIdentity] = useState(null);
  const [activeSide, setActiveSide] = useState("front");

  const documents = Array.isArray(identity?.documents) ? identity.documents : [];
  const frontDoc = documents.find((doc) => /front/i.test(doc.document_type || "")) || documents[0];
  const backDoc = documents.find((doc) => /back/i.test(doc.document_type || "")) || documents[1];
  const selectedDoc = activeSide === "front" ? frontDoc : backDoc;
  const details = useMemo(() => parseDetails(documents), [documents]);

  async function openAadhaar() {
    setOpen(true);
    setError("");
    setLoading(true);
    setIdentity(null);
    setActiveSide("front");

    const rawUserId = String(userId ?? "").trim();
    const compositeMatch = rawUserId.match(/\buser\s*\[\s*(\d+)\s*\]/i);
    const normalizedUserId = /^\d+$/.test(rawUserId)
      ? rawUserId
      : compositeMatch?.[1] || "";
    if (!normalizedUserId || Number(normalizedUserId) <= 0) {
      setError("This row does not contain a valid user ID.");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(
        `${(apiBaseUrl || "http://127.0.0.1:8000").replace(/\/$/, "")}/api/fetch_identity/${encodeURIComponent(normalizedUserId)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || `Unable to fetch Aadhaar details (HTTP ${response.status}).`);
      }
      if (!Array.isArray(payload?.documents)) {
        throw new Error("The identity endpoint response does not contain a documents list.");
      }
      setIdentity(payload);
    } catch (err) {
      setError(err?.message || "Unable to load Aadhaar details.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="aadhaar-open-button"
        title="View Aadhaar details"
        aria-label={`View Aadhaar details for ${userName || `user ${userId}`}`}
        onClick={openAadhaar}
        disabled={!userId || String(userId) === "-"}
      >
        <AadhaarIcon />
      </button>

      {open && (
        <div className="aadhaar-modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <section className="aadhaar-modal" role="dialog" aria-modal="true" aria-labelledby="aadhaar-modal-title">
            <header className="aadhaar-modal-header">
                <div className="aadhaar-modal-eyebrow">IDENTITY DOCUMENT  |  Aadhaar details  | User ID {userId}</div>
              <button type="button" className="aadhaar-close-button" onClick={() => setOpen(false)} aria-label="Close Aadhaar details">×</button>
            </header>

            {loading ? (
              <div className="aadhaar-state"><span className="aadhaar-spinner" />Loading Aadhaar details…</div>
            ) : error ? (
              <div className="aadhaar-error" role="alert">{error}</div>
            ) : (
              <div className="aadhaar-modal-body">
                <div className="aadhaar-card-tabs" role="tablist" aria-label="Aadhaar side">
                  <button type="button" role="tab" aria-selected={activeSide === "front"} className={activeSide === "front" ? "active" : ""} onClick={() => setActiveSide("front")}>Front side</button>
                  <button type="button" role="tab" aria-selected={activeSide === "back"} className={activeSide === "back" ? "active" : ""} onClick={() => setActiveSide("back")} disabled={!backDoc}>Back side</button>
                </div>

                <article className="aadhaar-card-preview">
                  <div className="aadhaar-card-rule" />
                  {selectedDoc?.file_url ? (
                    <div className="aadhaar-image-wrap">
                      <img src={selectedDoc.file_url} alt={`Aadhaar ${activeSide} side`} referrerPolicy="no-referrer" />
                    </div>
                  ) : (
                    <div className="aadhaar-no-image">{activeSide === "front" ? "Front image not available" : "Back image not available"}</div>
                  )}
                </article>

                  <div className="aadhaar-detail-grid aadhaar-detail-grid-four">
                    <div className="aadhaar-detail-item">
                      <span>Name</span>
                      <strong>{details.name || identity?.aadhaar_details?.name || identity?.user_name || userName || PLACEHOLDER}</strong>
                    </div>

                    <div className="aadhaar-detail-item">
                      <span>DOB</span>
                      <strong>{details.dob || identity?.aadhaar_details?.dob || PLACEHOLDER}</strong>
                    </div>

                    <div className="aadhaar-detail-item">
                      <span>Age</span>
                      <strong>
                        {(() => {
                          const dob = details.dob || identity?.aadhaar_details?.dob;
                          if (!dob) return PLACEHOLDER;

                          const parts = dob.split(/[/-]/);
                          let birthDate;

                          if (parts[0].length === 4) {
                            birthDate = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
                          } else {
                            birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                          }

                          if (Number.isNaN(birthDate.getTime())) return PLACEHOLDER;

                          const today = new Date();
                          let age = today.getFullYear() - birthDate.getFullYear();

                          if (
                            today.getMonth() < birthDate.getMonth() ||
                            (today.getMonth() === birthDate.getMonth() &&
                              today.getDate() < birthDate.getDate())
                          ) {
                            age--;
                          }

                          return age >= 0 ? `${age} years` : PLACEHOLDER;
                        })()}
                      </strong>
                    </div>

                    <div className="aadhaar-detail-item">
                      <span>Aadhaar Number</span>
                      <strong>{details.aadhaar || PLACEHOLDER}</strong>
                    </div>
                  <div className="aadhaar-detail-item aadhaar-detail-wide">
                  <span>Address</span>
                  <strong>
                    {(identity?.aadhaar_details?.address || "")
                      .replace(/^\s*:\s*/, "")
                      .trim() || PLACEHOLDER}
                  </strong>
                </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
